import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { ErrorState } from "../../../components/States";
import { DataTable, PageHeader } from "../../../components/ui";
import { LineChart } from "../../insights/charts";
import { backendApi, backendKeys, type MetricAgg, type MetricInfo } from "../api";
import { AGG_LABEL, BackendActions, BackendTabs, ConnectPrompt, ServiceSelect, aggsFor, unitName, useBackendRange, useFilters, when } from "../components";
import { MetricAlertDialog, MetricAlerts, newAlert, type AlertEditing } from "../MetricAlerts";

const KEYS = ["metric", "agg", "groupBy", "service"] as const;

export { aggsFor, unitName };

const KIND_LABEL: Record<string, string> = { counter: "Count", gauge: "Gauge", histogram: "Distribution" };

export function MetricsPage() {
  const auth = useAuth();
  const [range] = useBackendRange();
  const [f, setF] = useFilters(KEYS);
  const list = useQuery({ queryKey: backendKeys.metrics(auth.currentProjectId, range), queryFn: () => backendApi.metrics(range), enabled: !!auth.currentProjectId });
  const metrics = list.data?.items ?? [];
  const current = metrics.find((m) => m.name === f.metric) ?? metrics[0];
  const [alerting, setAlerting] = useState<AlertEditing | null>(null);

  return (
    <>
      <PageHeader title="Backend" actions={<BackendActions />}>
        <BackendTabs />
      </PageHeader>
      {list.isError ? (
        <div className="card section"><ErrorState error={list.error} retry={() => void list.refetch()} /></div>
      ) : !list.isPending && metrics.length === 0 ? (
        <ConnectPrompt title="No metrics yet" hint="Connected services send request rates, latency, and runtime health on their own. Your own counters and gauges appear here too." />
      ) : (
        <>
          {current && <MetricChart metric={current} agg={f.agg as MetricAgg | undefined} groupBy={f.groupBy} service={f.service} set={setF} onAlert={(agg) => setAlerting(newAlert(current, agg, f.service))} />}
          <div className="card table-scroll stack-table be-table">
            <DataTable<MetricInfo>
              ariaLabel="Metrics"
              rows={metrics}
              rowKey={(m) => m.name}
              loading={list.isPending}
              onRowClick={(m) => setF({ metric: m.name, agg: undefined, groupBy: undefined })}
              rowClassName={(m) => (m.name === current?.name ? "selected" : undefined)}
              columns={[
                { key: "name", header: "Metric", render: (m) => (<span className="cell-stack"><strong className="mono">{m.name}</strong><small>{m.services.join(", ")}</small></span>) },
                { key: "kind", header: "Type", label: "Type", width: 120, render: (m) => <span className="badge neutral">{KIND_LABEL[m.kind] ?? m.kind}</span> },
                { key: "unit", header: "Unit", label: "Unit", width: 90, render: (m) => <span className="small">{unitName(m.unit) || "—"}</span> },
                { key: "series", header: "Series", label: "Series", width: 90, align: "right", render: (m) => <span className="mono">{m.series.toLocaleString()}</span> },
                { key: "last", header: "Last reported", label: "Last reported", width: 170, render: (m) => <span className="small">{when(m.lastSeen)}</span> },
              ]}
            />
          </div>
          <MetricAlerts metrics={metrics} onEdit={setAlerting} />
        </>
      )}
      <MetricAlertDialog editing={alerting} setEditing={setAlerting} metrics={metrics} />
    </>
  );
}

function MetricChart({ metric, agg, groupBy, service, set, onAlert }: { metric: MetricInfo; agg?: MetricAgg; groupBy?: string; service?: string; set: (p: Partial<Record<(typeof KEYS)[number], string | undefined>>) => void; onAlert: (agg: MetricAgg) => void }) {
  const auth = useAuth();
  const can = useCan();
  const [range] = useBackendRange();
  const options = aggsFor(metric.kind);
  const a = agg && options.includes(agg) ? agg : options[0]!;
  const preset = groupBy === undefined || groupBy === "service" || groupBy === "environment" ? (groupBy ?? "") : "label";
  const [labelKey, setLabelKey] = useState(preset === "label" ? groupBy ?? "" : "");
  const [labelMode, setLabelMode] = useState(preset === "label");
  useEffect(() => {
    setLabelKey(preset === "label" ? groupBy ?? "" : "");
    if (preset !== "") setLabelMode(preset === "label");
  }, [groupBy, preset]);
  const params = { range, name: metric.name, agg: a, groupBy, service };
  const q = useQuery({ queryKey: backendKeys.metricQuery(auth.currentProjectId, params), queryFn: () => backendApi.metricQuery(params), enabled: !!auth.currentProjectId });
  const unit = a === "rate" ? "/s" : a === "count" ? "" : unitName(metric.unit) ? ` ${unitName(metric.unit)}` : "";
  const fmt = (v: number) => `${Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : Number(v.toPrecision(3)).toLocaleString()}${unit}`;

  return (
    <section className="card section be-metric" aria-label={`${metric.name} chart`}>
      <div className="be-metric-head">
        <div className="be-metric-title">
          <h2 className="be-h2 mono">{metric.name}</h2>
          {can.admin && <button type="button" className="btn sm" onClick={() => onAlert(a)}>Alert on this metric</button>}
        </div>
        <div className="ins-filters be-metric-controls">
          <label>
            Show
            <select value={a} onChange={(e) => set({ agg: e.target.value })} autoComplete="off">
              {options.map((o) => <option key={o} value={o}>{AGG_LABEL[o]}</option>)}
            </select>
          </label>
          <ServiceSelect value={service} onChange={(s) => set({ service: s })} services={metric.services} />
          <label>
            Split by
            <select
              value={labelMode ? "label" : preset}
              onChange={(e) => {
                const v = e.target.value;
                setLabelMode(v === "label");
                set({ groupBy: v === "label" ? labelKey.trim() || undefined : v || undefined });
              }}
              autoComplete="off"
            >
              <option value="">Nothing</option>
              <option value="service">Service</option>
              <option value="environment">Environment</option>
              <option value="label">A label…</option>
            </select>
          </label>
          {labelMode ? (
            <label>
              Label name
              <input
                value={labelKey}
                placeholder="http.route"
                onChange={(e) => setLabelKey(e.target.value)}
                onBlur={() => set({ groupBy: labelKey.trim() || undefined })}
                onKeyDown={(e) => e.key === "Enter" && set({ groupBy: labelKey.trim() || undefined })}
                autoComplete="off"
              />
            </label>
          ) : null}
        </div>
      </div>
      {q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : q.isPending ? (
        <div className="skeleton be-chart-skeleton" aria-hidden="true">loading</div>
      ) : (q.data.series ?? []).length === 0 ? (
        <p className="muted small">No values for this metric in this time range.</p>
      ) : (
        <LineChart
          ariaLabel={`${metric.name}, ${AGG_LABEL[a].toLowerCase()}`}
          height={220}
          formatValue={fmt}
          area={q.data.series.length === 1}
          series={q.data.series.map((s) => ({ name: s.group || metric.name, points: s.points.map(([t, v]) => ({ t, v })) }))}
        />
      )}
    </section>
  );
}
